import React, { useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import OptimizedImage from '../components/OptimizedImage';

const Shop = () => {
  const [selectedVariety, setSelectedVariety] = useState('all');
  const { dispatch } = useCart();
  const { currentCurrency } = useCurrency();

  const mangoes = [
    {
      id: '1',
      name: 'Alphonso',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1601493700631-2b16ec4b4716?auto=format&fit=crop&q=80',
      price: 108,
      description: 'The king of mangoes, rich and creamy with a distinctive aroma',
      origin: 'Maharashtra',
      season: 'March - June',
      weight: '1kg'
    },
    {
      id: '2',
      name: 'Banganapalli',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1519096845289-95806ee03a1a?auto=format&fit=crop&q=80',
      price: 120,
      description: 'Sweet and aromatic golden mangoes, fiber-free pulp',
      origin: 'Andhra Pradesh',
      season: 'March - June',
      weight: '1kg'
    },
    {
      id: '3',
      name: 'Kesar',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1553279768-865429fa0078?auto=format&fit=crop&q=80',
      price: 120,
      description: 'The queen of mangoes, known for its sweet and rich pulp',
      origin: 'Gujarat',
      season: 'March - June',
      weight: '1kg'
    },
    {
      id: '4',
      name: 'Nuzvidu Rasalu',
      variety: 'premium',
      image: 'https://images.unsplash.com/photo-1590244843380-09f8fc7a7c4c?auto=format&fit=crop&q=80',
      price: 125,
      description: 'Legendary mangoes from Nuzvidu, exceptionally sweet and aromatic',
      origin: 'Andhra Pradesh',
      season: 'March - June',
      weight: '1kg'
    },
    {
      id: '5',
      name: 'Totapuri',
      variety: 'classic',
      image: 'https://images.unsplash.com/photo-1591073113125-e46713c829ed?auto=format&fit=crop&q=80',
      price: 89,
      description: 'Long, pointed mangoes with a unique tangy-sweet taste, perfect for juices',
      origin: 'Karnataka',
      season: 'May - July',
      weight: '1kg'
    }
  ];

  const filteredMangoes = selectedVariety === 'all'
    ? mangoes
    : mangoes.filter(mango => mango.variety === selectedVariety);

  const handleAddToCart = (mango: typeof mangoes[0]) => {
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: mango.id,
        name: mango.name,
        price: mango.price,
        quantity: 1,
        image: mango.image
      }
    });
  };

  const formatPrice = (price: number) => {
    return `₹${price}/kg`;
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Our Premium Mangoes</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Experience the finest mangoes from India's most renowned orchards. 
            Each variety is carefully selected and delivered fresh to your doorstep.
          </p>
          <div className="mt-4 bg-yellow-50 text-yellow-700 p-4 rounded-lg inline-block">
            <p>Free delivery on orders above ₹2000</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex rounded-md shadow-sm">
            <button
              onClick={() => setSelectedVariety('all')}
              className={`px-4 py-2 text-sm font-medium ${
                selectedVariety === 'all'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border border-gray-300 rounded-l-lg`}
            >
              All Varieties
            </button>
            <button
              onClick={() => setSelectedVariety('premium')}
              className={`px-4 py-2 text-sm font-medium ${
                selectedVariety === 'premium'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border-t border-b border-r border-gray-300`}
            >
              Premium
            </button>
            <button
              onClick={() => setSelectedVariety('classic')}
              className={`px-4 py-2 text-sm font-medium ${
                selectedVariety === 'classic'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border-t border-b border-r border-gray-300 rounded-r-lg`}
            >
              Classic
            </button>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredMangoes.map((mango) => (
            <div key={mango.id} className="bg-white rounded-lg shadow-md overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
              <div className="relative aspect-w-4 aspect-h-3">
                <OptimizedImage
                  src={mango.image}
                  alt={mango.name}
                  className="w-full h-64 object-cover"
                />
                <div className="absolute top-4 right-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {mango.variety === 'premium' ? 'Premium' : 'Classic'}
                </div>
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-2">{mango.name}</h2>
                <p className="text-gray-600 mb-4">{mango.description}</p>
                <div className="mb-4 space-y-1">
                  <p className="text-sm text-gray-500 flex items-center">
                    <span className="font-medium mr-2">Weight:</span> {mango.weight}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center">
                    <span className="font-medium mr-2">Origin:</span> {mango.origin}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center">
                    <span className="font-medium mr-2">Season:</span> {mango.season}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-yellow-500">
                    {formatPrice(mango.price)}
                  </span>
                  <button
                    onClick={() => handleAddToCart(mango)}
                    className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center"
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Shop;